import { useEffect, useRef } from 'react';
import logo from './images/DEALogo.png';
import { Button, styled } from '@mui/material';
import { IApiCallStatus, useNonce, variables, PDFModal } from './common';
import {
  GenerateUser,
  Signup,
  useSignup,
  useSignin,
  useStatusMessage,
  useNameSync, 
  useNameChange,
} from './login';
import { compose as lcompose, isEmpty } from 'lodash/fp';
import { defaultTo, replace } from 'rambda';
import { left, right, map } from 'fp-ts/lib/Either';
import { match } from 'fp-ts/lib/boolean';
import { pipe } from 'fp-ts/lib/function';
import { ReactivateUser } from './login/ReactivateUser';

export function Landing({
  refreshTokenStatus,
}: {
  refreshTokenStatus: IApiCallStatus;
}) {
  useSignin();

  const bannerMessage = useStatusMessage();

  const signupCode = useSignup();
  const nameChangeCode = useNameChange();
  const nameSyncCode = useNameSync();

  const code = signupCode || nameChangeCode || nameSyncCode;

  const signinLink = useRef(null);

  const { nonce, callNonce } = useNonce();

  const signinUrl = lcompose(
    replace('&nonce=111111', `&nonce=${nonce}`),
    defaultTo('')
  );

  useEffect(() => {
    pipe(
      nonce,
      isEmpty,
      match(
        () => right(void 0),
        () => left(void 0)
      ),
      map(
        () =>
          signinLink.current !== null &&
          (signinLink.current as HTMLAnchorElement).click()
      )
    );
  }, [nonce]);

  return (
    <>
      <Articl>
        {/* GenerateUser now handles both signup AND name change/sync */}
        <GenerateUser code={code} />

        <Heade>
          <h3>
            <span>Welcome to CSOS Login</span>{' '}
            <span style={{ color: 'red' }}>{bannerMessage}</span>
            <img src={logo} alt="Drug Enforcement Administration" />
          </h3>
        </Heade>

        {refreshTokenStatus === null && (
          <LoginSection>
            <h3>Login...</h3>
          </LoginSection>
        )}

        {refreshTokenStatus !== null && (
          <LoginSection>
            <h3>Login</h3>
            <b>Instructions:</b> Select a login method to continue
            <p>
              <Action
                variant="contained"
                color="primary"
                disableRipple
                onClick={callNonce}>
                Sign In
              </Action>
            </p>
            <p>
              <Signup />
            </p>
            {/* <p>
              <ReactivateUser />
            </p> */}
            <p>
              Having trouble logging in? Contact CSOS Service Desk at
              1-877-DEA-ECOM (1-877-332-3266 ) or csossupport@deaecom.gov for
              assistance.
            </p>
            <p aria-hidden="true" hidden>
              <a
                href={signinUrl((import.meta as any).env.VITE_SIGNIN_URL)}
                ref={signinLink}
                tabIndex={-1}>
                dummy
              </a>
            </p>
            <ul>
              <li>
                <PDFModal
                  fileUrl={'Sign In.pdf'}
                  fileLabel={'SIGN IN instructions'}
                  fileDownload="signin_instructions.pdf"
                />
              </li>
              <li>
                <PDFModal
                  fileUrl={'Creating an Account.pdf'}
                  fileLabel={'SIGN UP instructions'}
                  fileDownload="signup_instructions.pdf"
                />
              </li>
              <li>
                <PDFModal
                  fileUrl={'Password Reset.pdf'}
                  fileLabel={'Password reset instructions'}
                  fileDownload="password_reset_signup_instructions.pdf"
                />
              </li>
            </ul>
          </LoginSection>
        )}

        <WarningSection>
          <h3>WARNING</h3>
          You are accessing U.S. Government information technology and/or
          information systems which includes:
          <ol>
            <li>(1) this information technology,</li>
            <li>(2) this information system,</li>
            <li>
              (3) all information technology devices connected to this network,
              and
            </li>
            <li>
              (4) all devices and storage media attached to this information
              system or to information technology on this network.
            </li>
          </ol>
          <p>
            This information technology and information system is provided for
            U.S. Government-authorized use only. Unauthorized or improper use
            may result in disciplinary action as well as civil and/or criminal
            penalties. By using this information technology and/or information
            system, you understand and consent to the following:
          </p>
          <ol>
            <li>
              &#x2022; You have no reasonable expectation of privacy regarding
              any communications transmitted through or data stored on this
              information technology and/or information system. At any time, the
              government may monitor, intercept, search and/or seize data
              transiting or stored on this information technology and/or
              information system.
            </li>
            <li>
              &#x2022; Any communication transmitted through or data stored on
              this information technology and/or information system may be
              disclosed or used for any lawful government purpose.
            </li>
            <li>
              &#x2022; Your consent is final and irrevocable. You may not rely
              on any statements or informal policies purporting to provide you
              with any expectation of privacy regarding communications on this
              system.
            </li>
          </ol>
          <p>
            For further information, see the Department order on Use and
            Monitoring of DOJ Information Technology, Information Systems, and
            Access to an Authorized Users' Electronic Information.
          </p>
        </WarningSection>
      </Articl>
      {/* <pre>{JSON.stringify(user, null, 2)}</pre> */}
    </>
  );
}

const Articl = styled('article')(({ theme }) => ({
  display: 'grid',
  gridTemplateAreas: `
    "header"
    "login"
    "warning"
    `,
  gap: '1em',

  '@media (min-width: 800px)': {
    gridTemplateColumns: '6fr 4fr',
    gridTemplateAreas: `
    "header  header"
    "warning login"
    
    `,
  },
}));

const Heade = styled('header')(({ theme }) => ({
  gridArea: 'header',

  '& > h3': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },

  '&  img': {
    width: '8rem',
  },
}));

const LoginSection = styled('section')(({ theme }) => ({
  gridArea: 'login',

  '& > * + *': {
    marginTop: '1em',
  },

  '& > * + button': {
    marginTop: '2em',
  },

  '& ul li + li': {
    marginTop: '0.5rem',
  },
  ...card,
}));

const WarningSection = styled('section')(({ theme }) => ({
  gridArea: 'warning',

  '& > * + *': {
    marginTop: '1em',
  },

  '& ul': {
    listStyleType: 'none',
  },
  ...card,
}));

const card = {
  border: variables.cardBorder,
  borderRadius: variables.cardBorderRadius,
  padding: variables.spacing(1),
};

const Action = styled(Button)(({ theme }) => ({
  width: '100%',

  '&:focus-visible': {
    filter: 'contrast(200%)',
  },
}));


